import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, defer, EMPTY, take, throwError } from 'rxjs';
import { DialogManagerService } from '@services/utils/dialog-manager.service';
import { DialogUnavailableError } from '@services/utils/dialog-unavailable.error';
import { EnvironmentService } from '@services/utils/environment.service';
import { NEWS_DETAIL_ROUTE } from '@shared/consts/routes';
import { BuildType } from '@shared/enums/build.type';
import { InfoTypeStringEnum } from '@shared/enums/info-type.enum';
import {
  isDismissalActive,
  readStoredDismissal,
  registerShowDismissal,
  storeDismissal,
} from '@shared/utils/dismissal-schedule/dismissal-schedule';
import { UserFacade } from '../../../../../store/user/user.facade';
import { OnlinePaymentAnnouncementDialogComponent } from '../online-payment-announcement-dialog.component';
import { OnlinePaymentAnnouncementAction } from '../online-payment-announcement-action.enum';
import {
  getOnlinePaymentAnnouncementStorageKey,
  ONLINE_PAYMENT_ANNOUNCEMENT_CONTENT_STYLE,
  ONLINE_PAYMENT_ANNOUNCEMENT_DIALOG_CLASS,
  ONLINE_PAYMENT_ANNOUNCEMENT_DIALOG_WIDTH,
  ONLINE_PAYMENT_ANNOUNCEMENT_SCHEDULE,
  ONLINE_PAYMENT_NEWS_ID,
} from '../online-payment-announcement.const';
// TISS-7648: the ui-state backend is not in the release. Uncomment together with the ui-state block in the class.
// import { DestroyRef, ErrorHandler } from '@angular/core';
// import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
// import { filter, map, Observable, of, switchMap, tap } from 'rxjs';
// import { UsersService } from '@services/api/internal/users.service';
// import { UserAnnouncementStatus } from '@shared/enums/user-announcement-status.enum';
// import { UserAnnouncement } from '@shared/interfaces/users/user-announcement.interface';
// import { removeStoredDismissal } from '@shared/utils/dismissal-schedule/dismissal-schedule';
// import {
//   getOnlinePaymentAnnouncementRequest,
//   ONLINE_PAYMENT_ANNOUNCEMENT_KEY,
//   ONLINE_PAYMENT_ANNOUNCEMENT_LEGACY_MAX_DISMISSALS,
// } from '../online-payment-announcement.const';

@Injectable()
export class OnlinePaymentAnnouncementDialogService {
  private readonly dialogManagerService = inject(DialogManagerService);
  private readonly environmentService = inject(EnvironmentService);
  private readonly userFacade = inject(UserFacade);
  private readonly router = inject(Router);

  private openNews(): void {
    void this.router.navigate([NEWS_DETAIL_ROUTE(ONLINE_PAYMENT_NEWS_ID)], { queryParams: { type: InfoTypeStringEnum.news } });
  }

  // TISS-7648: the ui-state backend is not in the release, so the shows are kept in browser storage (`open` below).
  // Once the backend ships, uncomment this block with its imports and constants, replace the storage `open` with the
  // commented one, uncomment the ui-state e2e and take the ui-state versions of this service spec and
  // e2e/tr/online-payment-announcement.spec.ts from commit 2308193f.
  //
  // private readonly usersService = inject(UsersService);
  // private readonly destroyRef = inject(DestroyRef);
  // private readonly errorHandler = inject(ErrorHandler);
  //
  // private isCurrentUser(userId: string): boolean {
  //   return this.userFacade.$user()?.userId === userId;
  // }
  //
  // private loadRecord(userId: string): Observable<UserAnnouncement | null> {
  //   const findRecord = (records: UserAnnouncement[]) => records.find(record => record.key === ONLINE_PAYMENT_ANNOUNCEMENT_KEY) ?? null;
  //
  //   return this.usersService.getAnnouncements(ONLINE_PAYMENT_ANNOUNCEMENT_KEY).pipe(
  //     filter(() => this.isCurrentUser(userId)),
  //     map(findRecord),
  //     switchMap(record => {
  //       if (record?.status !== UserAnnouncementStatus.New) {
  //         return of(record);
  //       }
  //
  //       const storageKey = getOnlinePaymentAnnouncementStorageKey(userId);
  //       const legacyRecord = readStoredDismissal(storageKey);
  //       if (!legacyRecord || !Number.isInteger(legacyRecord.count) || legacyRecord.count < 1) {
  //         return of(record);
  //       }
  //
  //       const status =
  //         legacyRecord.count >= ONLINE_PAYMENT_ANNOUNCEMENT_LEGACY_MAX_DISMISSALS
  //           ? UserAnnouncementStatus.Completed
  //           : UserAnnouncementStatus.Deferred;
  //       const dismissedAt = Date.parse(legacyRecord.dismissedAt);
  //       if (status === UserAnnouncementStatus.Deferred && !Number.isFinite(dismissedAt)) {
  //         return of(record);
  //       }
  //
  //       return this.usersService
  //         .setAnnouncement(ONLINE_PAYMENT_ANNOUNCEMENT_KEY, getOnlinePaymentAnnouncementRequest(status, dismissedAt))
  //         .pipe(
  //           filter(() => this.isCurrentUser(userId)),
  //           // Blocked storage keeps the record; the server record takes precedence on the next login.
  //           tap(() => removeStoredDismissal(storageKey)),
  //           // PUT has no body. Let the backend decide whether the migrated key is available now.
  //           switchMap(() => this.usersService.getAnnouncements(ONLINE_PAYMENT_ANNOUNCEMENT_KEY)),
  //           map(findRecord),
  //         );
  //     }),
  //   );
  // }
  //
  // private openDialog(userId: string, statusAtOpen: UserAnnouncementStatus): void {
  //   // The manager reports duplicate opening with DialogUnavailableError. The dialog lives in the root DialogService and can outlive
  //   // the layout, so the close is not bound to the layout DestroyRef: `onClose` completes together with the dialog.
  //   defer(
  //     () =>
  //       this.dialogManagerService.openCustomDialog(OnlinePaymentAnnouncementDialogComponent, {
  //         showHeader: false,
  //         // The component focuses its own container instead of the close button (see the component constructor).
  //         focusOnShow: false,
  //         width: ONLINE_PAYMENT_ANNOUNCEMENT_DIALOG_WIDTH,
  //         styleClass: ONLINE_PAYMENT_ANNOUNCEMENT_DIALOG_CLASS,
  //         // The manager hides the content overflow; on short screens (a phone in landscape) the text and the buttons
  //         // would be cut off by the panel max height, so the content scrolls vertically instead.
  //         contentStyle: ONLINE_PAYMENT_ANNOUNCEMENT_CONTENT_STYLE,
  //       }).onClose,
  //   )
  //     .pipe(
  //       catchError((error: unknown) => (error instanceof DialogUnavailableError ? EMPTY : throwError(() => error))),
  //       take(1),
  //       filter(() => this.isCurrentUser(userId)),
  //     )
  //     .subscribe((action?: OnlinePaymentAnnouncementAction) => {
  //       // Tabs that opened the same show send the same status; saving does not block navigation.
  //       const status = statusAtOpen === UserAnnouncementStatus.New ? UserAnnouncementStatus.Deferred : UserAnnouncementStatus.Completed;
  //       // The save is a single HTTP request that completes on its own and must outlive the layout like the dialog.
  //       // A failed save stays invisible to the user but still reaches the global handler.
  //       this.usersService
  //         .setAnnouncement(ONLINE_PAYMENT_ANNOUNCEMENT_KEY, getOnlinePaymentAnnouncementRequest(status, Date.now()))
  //         .subscribe({ error: (error: unknown) => this.errorHandler.handleError(error) });
  //
  //       if (action === OnlinePaymentAnnouncementAction.LearnMore) {
  //         this.openNews();
  //       }
  //     });
  // }
  //
  // /** Opens the announcement only when the backend returns its key for the Turkish user. */
  // open(): void {
  //   const userId = this.userFacade.$user()?.userId;
  //   if (!this.environmentService.isBuildType(BuildType.tr) || !userId) {
  //     return;
  //   }
  //
  //   this.loadRecord(userId)
  //     .pipe(
  //       take(1),
  //       takeUntilDestroyed(this.destroyRef),
  //       catchError((error: unknown) => {
  //         // The announcement stays hidden for this entry without a message; the error still reaches the global handler.
  //         this.errorHandler.handleError(error);
  //         return EMPTY;
  //       }),
  //     )
  //     .subscribe(record => {
  //       if (!this.isCurrentUser(userId)) {
  //         return;
  //       }
  //
  //       if (record) {
  //         this.openDialog(userId, record.status);
  //       }
  //     });
  // }

  /** Opens the online payment announcement if the Turkish user has not used up the shows yet. */
  open(): void {
    const userId = this.userFacade.$user()?.userId;
    if (!this.environmentService.isBuildType(BuildType.tr) || !userId) {
      return;
    }

    const storageKey = getOnlinePaymentAnnouncementStorageKey(userId);
    const recordAtOpen = readStoredDismissal(storageKey);
    if (isDismissalActive(recordAtOpen, Date.now(), ONLINE_PAYMENT_ANNOUNCEMENT_SCHEDULE)) {
      return;
    }

    // The manager reports duplicate opening with DialogUnavailableError. The dialog lives in the root DialogService and can outlive
    // the layout, so the close is not bound to the layout DestroyRef: `onClose` completes together with the dialog.
    defer(
      () =>
        this.dialogManagerService.openCustomDialog(OnlinePaymentAnnouncementDialogComponent, {
          showHeader: false,
          // The component focuses its own container instead of the close button (see the component constructor).
          focusOnShow: false,
          width: ONLINE_PAYMENT_ANNOUNCEMENT_DIALOG_WIDTH,
          styleClass: ONLINE_PAYMENT_ANNOUNCEMENT_DIALOG_CLASS,
          // The manager hides the content overflow; on short screens (a phone in landscape) the text and the buttons
          // would be cut off by the panel max height, so the content scrolls vertically instead.
          contentStyle: ONLINE_PAYMENT_ANNOUNCEMENT_CONTENT_STYLE,
        }).onClose,
    )
      .pipe(
        catchError((error: unknown) => (error instanceof DialogUnavailableError ? EMPTY : throwError(() => error))),
        take(1),
      )
      .subscribe((action?: OnlinePaymentAnnouncementAction) => {
        // Any close (cross, "Close", "Learn more" or the mask) counts as a show; closing the same show in several tabs
        // (or in a stale tab) counts it once.
        storeDismissal(storageKey, registerShowDismissal(recordAtOpen, readStoredDismissal(storageKey), Date.now()));

        if (action === OnlinePaymentAnnouncementAction.LearnMore) {
          this.openNews();
        }
      });
  }
}
